/**
 * Pure utility functions and constants shared across the Route Builder area.
 * All logic here is side-effect free and testable in isolation.
 */

import { isRenderableCoordinate } from '@backoffice-frontend/overwatch-map-common'

// ── Route error message parsing ───────────────────────────────────────────────

/**
 * Parse a route validation error message to extract affected waypoint indices.
 * Returns a human-readable summary like "Error at waypoint(s) 2, 3" when the
 * message contains violation leg indices, otherwise returns the original message.
 */
export const formatRouteErrorMessage = (errorMessage: string): string => {
  if (!errorMessage.toLowerCase().includes('route violation')) {
    return errorMessage
  }

  const regex = /Route Violations for leg at index (\d+) \[violation_type: \w+/g
  const errorIndices: number[] = []
  for (const match of errorMessage.matchAll(regex)) {
    errorIndices.push(parseInt(match[1]) + 1)
  }

  if (errorIndices.length === 0) return errorMessage
  if (errorIndices.length === 1) return `Error at waypoint ${errorIndices[0]}`
  if (errorIndices.length === 2) {
    return `Error at waypoints ${errorIndices[0]} and ${errorIndices[1]}`
  }
  const indicesString =
    errorIndices.slice(0, -1).join(', ') +
    `, and ${errorIndices[errorIndices.length - 1]}`
  return `Error at waypoints ${indicesString}`
}

// ── Hub locations ──────────────────────────────────────────────────────────────

export type Hub = {
  name: string
  coords: [number, number] // [lng, lat]
  flyTo?: [number, number] // optional separate flyTo center [lng, lat]
}

export const HUBS: Hub[] = [
  { name: 'Austin', coords: [-97.69988638759784, 30.255213153633644] },
  { name: 'Munich U69', coords: [11.595108174831337, 48.17169897892049] },
  {
    name: 'Munich Test Track',
    coords: [11.746229724627467, 48.34745183035297],
  },
  {
    name: 'Munich Proving Ground',
    coords: [11.561412076677144, 48.27460667526258],
  },
  { name: 'Hamburg', coords: [10.094389010715542, 53.58630307778515] },
  {
    name: 'Los Angeles',
    coords: [-118.33023568549244, 34.09149457944449],
    flyTo: [-118.406927, 34.047153],
  },
  { name: 'Lake Nona', coords: [-81.3117446, 28.3636703] },
]

// ── Violation label maps (ported from ADMT RouteBuilderUtils) ─────────────────

/** Route violation type → human-readable message. Keys match GraphQL RouteViolationType enum values. */
export const ROUTE_VIOLATION_LABELS: Record<string, string> = {
  VIOLATION_TYPE_WRONG_DIRECTION_AT_ORIGIN:
    'Departs at origin in wrong direction',
  VIOLATION_TYPE_WRONG_DIRECTION_AT_DESTINATION:
    'Arrives at destination in wrong direction',
  VIOLATION_TYPE_INTERMEDIATE_LOCAL_ZONE_USED:
    'Passes through zone forbidding through traffic',
  VIOLATION_TYPE_BLOCKED_ROAD_USED: 'Uses blocked road(s)',
  VIOLATION_TYPE_TIME_DEPENDENT_RESTRICTION_VIOLATED:
    'Uses road disallowed at current time',
  VIOLATION_TYPE_ROAD_FORBIDDEN_FOR_VEHICLE_CONFIGURATION_USED:
    'Uses road forbidden for vehicle configuration',
  VIOLATION_TYPE_FORBIDDEN_TURN_USED: 'Contains illegal or impossible turn',
  VIOLATION_TYPE_TURN_FORBIDDEN_FOR_VEHICLE_CONFIGURATION_USED:
    'Contains turn forbidden for vehicle configuration',
  VIOLATION_TYPE_INVALID: 'Violation type not specified',
  // short-key fallbacks (backend may omit VIOLATION_TYPE_ prefix)
  WRONG_DIRECTION_AT_ORIGIN: 'Departs at origin in wrong direction',
  WRONG_DIRECTION_AT_DESTINATION: 'Arrives at destination in wrong direction',
  INTERMEDIATE_LOCAL_ZONE_USED:
    'Passes through zone forbidding through traffic',
  BLOCKED_ROAD_USED: 'Uses blocked road(s)',
  TIME_DEPENDENT_RESTRICTION_VIOLATED: 'Uses road disallowed at current time',
  ROAD_FORBIDDEN_FOR_VEHICLE_CONFIGURATION_USED:
    'Uses road forbidden for vehicle configuration',
  FORBIDDEN_TURN_USED: 'Contains illegal or impossible turn',
  TURN_FORBIDDEN_FOR_VEHICLE_CONFIGURATION_USED:
    'Contains turn forbidden for vehicle configuration',
  NO_ROUTE_FOUND: 'No route found',
  OTHER: 'Route violation',
}

/** SDS/Mobileye failure reason → human-readable message. Keys match GraphQL ValidationFailureReason enum values. */
export const SDS_REASON_LABELS: Record<string, string> = {
  VALIDATION_FAILURE_REASON_NO_MAP_COVERAGE: 'No Map Coverage',
  VALIDATION_FAILURE_REASON_SEGMENT_TOO_SHORT: 'Segment Too Short',
  VALIDATION_FAILURE_REASON_INVALID: 'Not drivable, no reason given',
  // short-key fallbacks
  NO_MAP_COVERAGE: 'No Map Coverage',
  SEGMENT_TOO_SHORT: 'Segment Too Short',
  SEGMENT_GAP: 'Undrivable connection between route segments',
  INVALID: 'Not drivable, no reason given',
}

/**
 * Calculate the true geographic midpoint along a polyline coordinate array.
 * Returns the point at 50% of the total Euclidean path length.
 */
export const calculateLineMidpoint = (
  coordinates: [number, number][],
): [number, number] => {
  if (coordinates.length === 0) return [0, 0]
  if (coordinates.length === 1) return coordinates[0]

  const distances: number[] = [0]
  let totalDistance = 0
  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1]
    const [lon2, lat2] = coordinates[i]
    const d = Math.sqrt((lon2 - lon1) ** 2 + (lat2 - lat1) ** 2)
    totalDistance += d
    distances.push(totalDistance)
  }

  const half = totalDistance / 2
  for (let i = 1; i < distances.length; i++) {
    if (distances[i] >= half) {
      const ratio =
        (half - distances[i - 1]) / (distances[i] - distances[i - 1])
      const [lon1, lat1] = coordinates[i - 1]
      const [lon2, lat2] = coordinates[i]
      return [lon1 + (lon2 - lon1) * ratio, lat1 + (lat2 - lat1) * ratio]
    }
  }

  return coordinates[coordinates.length - 1]
}

// ── Distance formatting ────────────────────────────────────────────────────────

export const METERS_TO_MILES = 0.000621371

export const getDistanceMiles = (distanceMeters: number): string => {
  const distanceMiles = Math.ceil(distanceMeters * METERS_TO_MILES)
  if (distanceMiles < 1) return '< 1 mile'
  return `${distanceMiles} Mile${distanceMiles > 1 ? 's' : ''}`
}

// ── ODD element type tokens ─────────────────────────────────────────────────────

import { TripOddElementType } from '@backoffice-frontend/graphql'
import { ODD_ICON_DEFS } from '@backoffice-frontend/overwatch-map-common'

/**
 * Serialized ODD-token → GraphQL enum, for tokens that map to a stop's ODD type
 * on save. Not every waypoint ODD type is here: map-only maneuver types
 * (PROTECTED_LEFT/RIGHT, MERGE) have icons but no enum, so this is narrower than
 * the set a waypoint can carry — use `isSerializableOddElementType` for
 * round-trip validation, not this map's keys.
 */
export const TRIP_ODD_ELEMENT_TYPES: Record<string, TripOddElementType> = {
  STOP_SIGN: TripOddElementType.OddElementTypeStopSign,
  TRAFFIC_LIGHT: TripOddElementType.OddElementTypeTrafficLight,
  YIELD_SIGN: TripOddElementType.OddElementTypeYieldSign,
  ROUNDABOUT: TripOddElementType.OddElementTypeRoundabout,
  NO_TURN_ON_RED: TripOddElementType.OddElementTypeNoTurnOnRed,
  ONE_WAY: TripOddElementType.OddElementTypeOneWay,
  OPS_HUB: TripOddElementType.OddElementTypeOpsHub,
  ALL_WAY_STOP: TripOddElementType.OddElementTypeAllWayStop,
  NO_ENTRY: TripOddElementType.OddElementTypeNoEntry,
  PEDESTRIAN_CROSSING: TripOddElementType.OddElementTypePedestrianCrossing,
  RAILROAD_CROSSING: TripOddElementType.OddElementTypeRailroadCrossing,
  BUS_STOP: TripOddElementType.OddElementTypeBusStop,
  RIGHT_ON_RED: TripOddElementType.OddElementTypeRightOnRed,
  SCHOOL_ZONE: TripOddElementType.OddElementTypeSchoolZone,
}

/**
 * Every ODD token a waypoint may legitimately carry: the icon-backed types a
 * user can click on the map (ODD_ICON_DEFS) plus the enum-mapped types
 * (OPS_HUB has no icon def). Neither set alone is a superset, so union them.
 * A Set (own keys only) also rejects inherited keys like `toString`, which
 * `token in obj` would accept.
 */
const SERIALIZABLE_ODD_ELEMENT_TYPES = new Set([
  ...Object.keys(ODD_ICON_DEFS),
  ...Object.keys(TRIP_ODD_ELEMENT_TYPES),
])

export const isSerializableOddElementType = (type: string): boolean =>
  SERIALIZABLE_ODD_ELEMENT_TYPES.has(type)

// ── Coordinate parsing ─────────────────────────────────────────────────────────

/**
 * Regex matching coordinate strings with up to two optional trailing tokens:
 * "lat, lng", "lat, lng, heading", "lat, lng, ODD_TYPE" or
 * "lat, lng, heading, ODD_TYPE". The trailing tokens are captured generically
 * and classified in `parseCoordinates` (numeric → heading, known ODD token →
 * ODD element type) so the ODD token may appear with or without a heading
 * before it. An unrecognized trailing token rejects the whole line, matching
 * the strict validation callers relied on before ODD tokens were added.
 */
export const coordRegEx =
  /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)(?:\s*,\s*([^,\s]+))?(?:\s*,\s*([^,\s]+))?\s*$/

const numericTokenRegEx = /^-?\d+(?:\.\d+)?$/

/**
 * Parse a coordinate string into components. Accepts an optional heading and an
 * optional ODD element type token after "lat, lng". Returns null if invalid,
 * out-of-range, or carrying an unrecognized trailing token.
 */
export const parseCoordinates = (
  input: string,
): {
  lat: number
  lng: number
  heading?: number
  oddElementType?: string
} | null => {
  const match = coordRegEx.exec(input.trim())
  if (!match) return null

  const lat = parseFloat(match[1])
  const lng = parseFloat(match[2])

  if (!isRenderableCoordinate(lat, lng)) return null

  let heading: number | undefined
  let oddElementType: string | undefined

  for (const token of [match[3], match[4]]) {
    if (token === undefined) continue
    if (numericTokenRegEx.test(token)) {
      if (heading !== undefined) return null
      const parsed = parseFloat(token)
      if (parsed < 0 || parsed >= 360) return null
      heading = parsed
    } else if (isSerializableOddElementType(token)) {
      if (oddElementType !== undefined) return null
      oddElementType = token
    } else {
      return null
    }
  }

  return { lat, lng, heading, oddElementType }
}

// ── Lane direction detection ───────────────────────────────────────────────────
// Ported from overwatch RouteBuilderUtils.tsx — finds bearing directions from
// the lane network rendered on the MapLibre map near a given coordinate.

import type maplibregl from 'maplibre-gl'

export type StreetDirection = {
  bearing: number // 0-360 degrees, 0=North
  distance: number // meters from waypoint to nearest point on segment
}

export type StreetDirectionResult = {
  directions: StreetDirection[]
  snapPoint: [number, number] | null // [lng, lat]
}

const calculateDistanceMeters = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const calculateBearingDegrees = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const lat1Rad = (lat1 * Math.PI) / 180
  const lat2Rad = (lat2 * Math.PI) / 180
  const y = Math.sin(dLng) * Math.cos(lat2Rad)
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

const getLaneLayerIds = (map: maplibregl.Map): string[] =>
  (map.getStyle()?.layers ?? [])
    .filter(
      l =>
        l.id.endsWith('-line') && l.type === 'line' && l.id.includes('lanes'),
    )
    .map(l => l.id)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const computeDirectionsFromFeatures = (
  features: { geometry: unknown; properties?: Record<string, unknown> }[],
  lngLat: { lng: number; lat: number },
  searchRadiusMeters: number,
): StreetDirectionResult => {
  const EMPTY: StreetDirectionResult = { directions: [], snapPoint: null }
  const normalizeBearing = (b: number) => ((b % 360) + 360) % 360
  const raw: StreetDirection[] = []
  let globalBestDist = Infinity
  let globalSnapPoint: [number, number] | null = null

  for (const feature of features) {
    const geom = feature.geometry as { type: string; coordinates: unknown }
    if (
      !geom ||
      (geom.type !== 'LineString' && geom.type !== 'MultiLineString')
    )
      continue
    const isOneway = feature.properties?.oneway === true
    const coordArrays: number[][][] =
      geom.type === 'LineString'
        ? [geom.coordinates as number[][]]
        : (geom.coordinates as number[][][])

    for (const coords of coordArrays) {
      if (coords.length < 2) continue
      let bestDist = Infinity
      let bestSegIdx = 0
      let bestProjLng = 0
      let bestProjLat = 0

      for (let i = 0; i < coords.length - 1; i++) {
        const [ax, ay] = coords[i]
        const [bx, by] = coords[i + 1]
        const [px, py] = [lngLat.lng, lngLat.lat]
        const dx = bx - ax
        const dy = by - ay
        const lenSq = dx * dx + dy * dy
        let projLng: number, projLat: number
        if (lenSq === 0) {
          projLng = ax
          projLat = ay
        } else {
          const t = Math.max(
            0,
            Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq),
          )
          projLng = ax + t * dx
          projLat = ay + t * dy
        }
        const dist = calculateDistanceMeters(
          lngLat.lat,
          lngLat.lng,
          projLat,
          projLng,
        )
        if (dist < bestDist) {
          bestDist = dist
          bestSegIdx = i
          bestProjLng = projLng
          bestProjLat = projLat
        }
      }

      if (bestDist > searchRadiusMeters) continue
      if (bestDist < globalBestDist) {
        globalBestDist = bestDist
        globalSnapPoint = [bestProjLng, bestProjLat]
      }

      const [lng1, lat1] = coords[bestSegIdx]
      const [lng2, lat2] = coords[bestSegIdx + 1]
      const bearing = normalizeBearing(
        calculateBearingDegrees(lat1, lng1, lat2, lng2),
      )
      raw.push({ bearing, distance: bestDist })
      if (!isOneway)
        raw.push({
          bearing: normalizeBearing(bearing + 180),
          distance: bestDist,
        })
    }
  }

  if (raw.length === 0) return EMPTY

  // Only use directions from the closest feature (the one that owns the snap point).
  // A tolerance would include directions from neighbouring streets, producing extra
  // arrows that don't correspond to the lane the dashed line points to.
  const minDist = Math.min(...raw.map(d => d.distance))
  const nearest = raw.filter(d => d.distance === minDist)

  const CARDINALS = [0, 90, 180, 270]
  const angularDiff = (a: number, b: number) => {
    const d = Math.abs(a - b)
    return Math.min(d, 360 - d)
  }
  const final: StreetDirection[] = []
  for (const cardinal of CARDINALS) {
    let best: StreetDirection | null = null
    let bestDiff = Infinity
    for (const dir of nearest) {
      const diff = angularDiff(dir.bearing, cardinal)
      if (
        diff < bestDiff ||
        (diff === bestDiff && dir.distance < (best?.distance ?? Infinity))
      ) {
        bestDiff = diff
        best = dir
      }
    }
    if (best && !final.some(f => angularDiff(f.bearing, best!.bearing) < 30)) {
      final.push({ ...best })
    }
  }

  return { directions: final, snapPoint: globalSnapPoint }
}

/**
 * Find nearby street/lane bearing directions by reading directly from the GeoJSON source data.
 * Unlike findNearbyStreetDirections this is viewport-independent — it works even when the
 * lanes are not currently rendered (e.g. waypoint placed off-lane or on an unloaded tile).
 */
export const findNearbyStreetDirectionsFromSource = (
  map: maplibregl.Map,
  lngLat: { lng: number; lat: number },
  searchRadiusMeters = 50,
): StreetDirectionResult => {
  const EMPTY: StreetDirectionResult = { directions: [], snapPoint: null }
  const laneLayerIds = getLaneLayerIds(map)
  if (laneLayerIds.length === 0) return EMPTY

  const sourceIds = new Set<string>()
  for (const layerId of laneLayerIds) {
    const layer = map.getLayer(layerId)
    if (layer && 'source' in layer && typeof layer.source === 'string') {
      sourceIds.add(layer.source)
    }
  }

  const allFeatures: {
    geometry: unknown
    properties?: Record<string, unknown>
  }[] = []

  for (const sourceId of sourceIds) {
    const source = map.getSource(sourceId)
    if (source?.type !== 'geojson') continue
    // Access MapLibre's internal _data field (GeoJSON source only).
    // This is the full source dataset, not limited to the current viewport.
    // biome-ignore lint/suspicious/noExplicitAny: accessing MapLibre private _data API for viewport-independent lane lookup
    const data = (source as any)._data as
      | {
          type: string
          features?: unknown[]
          geometry?: unknown
          properties?: Record<string, unknown>
        }
      | undefined
    if (data) {
      if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
        allFeatures.push(
          ...(data.features as {
            geometry: unknown
            properties?: Record<string, unknown>
          }[]),
        )
      } else if (data.type === 'Feature') {
        allFeatures.push(
          data as { geometry: unknown; properties?: Record<string, unknown> },
        )
      }
    } else {
      // Fallback: querySourceFeatures (only returns tiled/rendered features)
      allFeatures.push(
        ...(map.querySourceFeatures(sourceId) as {
          geometry: unknown
          properties?: Record<string, unknown>
        }[]),
      )
    }
  }

  if (allFeatures.length === 0) return EMPTY
  return computeDirectionsFromFeatures(allFeatures, lngLat, searchRadiusMeters)
}

/**
 * Find nearby street/lane bearing directions using MapLibre queryRenderedFeatures.
 * Returns up to 4 directions (one per cardinal quadrant) from the lane network
 * at the given coordinate. Falls back to empty array if no lanes are in view.
 */
export const findNearbyStreetDirections = (
  map: maplibregl.Map,
  lngLat: { lng: number; lat: number },
  searchRadiusMeters = 50,
): StreetDirectionResult => {
  const laneLayerIds = getLaneLayerIds(map)
  const EMPTY: StreetDirectionResult = { directions: [], snapPoint: null }
  if (laneLayerIds.length === 0) return EMPTY

  const center = map.project([lngLat.lng, lngLat.lat])
  const offsetPoint = map.project([
    lngLat.lng,
    lngLat.lat + searchRadiusMeters / 111_320,
  ])
  const pixelRadius = Math.max(Math.abs(center.y - offsetPoint.y), 30)
  const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
    [center.x - pixelRadius, center.y - pixelRadius],
    [center.x + pixelRadius, center.y + pixelRadius],
  ]

  const features = map.queryRenderedFeatures(bbox, { layers: laneLayerIds })
  if (features.length === 0) return EMPTY

  return computeDirectionsFromFeatures(
    features as unknown as {
      geometry: unknown
      properties?: Record<string, unknown>
    }[],
    lngLat,
    searchRadiusMeters,
  )
}

// ── Ellipse orbit geometry ─────────────────────────────────────────────────────

/**
 * Compute CSS translate offset (dx, dy) from a marker center to a heading-arrow
 * center, given a bearing and ellipse radii.
 *
 * Bearing 0 = North, 90 = East (same convention as the backend heading field).
 */
export const ellipseOrbit = (
  rx: number,
  ry: number,
  bearingDeg: number,
): { dx: number; dy: number } => {
  const rad = ((bearingDeg - 90) * Math.PI) / 180
  const cosA = Math.cos(rad)
  const sinA = Math.sin(rad)
  const r = (rx * ry) / Math.sqrt((ry * cosA) ** 2 + (rx * sinA) ** 2)
  return { dx: cosA * r, dy: sinA * r }
}

// ── Violation message maps ─────────────────────────────────────────────────────

/** Maps route violation type numeric codes to human-readable UI strings. */
export const VIOLATION_TYPE_MESSAGES: Record<number, string> = {
  0: 'Violation type not specified',
  1: 'Departs at origin in wrong direction',
  2: 'Arrives at destination in wrong direction',
  3: 'Passes through zone forbidding through traffic',
  4: 'Uses blocked road(s)',
  5: 'Uses road disallowed at current time',
  6: 'Uses road forbidden for vehicle configuration',
  7: 'Contains illegal or impossible turn',
  8: 'Contains forbidden maneuver for vehicle configuration',
}

/** Maps Mobileye violation reason numeric codes to human-readable UI strings. */
export const VIOLATION_REASON_MESSAGES: Record<number, string> = {
  0: 'Unknown',
  1: 'No Map Coverage',
  2: 'Segment Too Short',
}

/**
 * Extract a validation failure reason key from an SDS validation message string.
 * Messages typically look like "VALIDATION_FAILURE_REASON_NO_MAP_COVERAGE at segment 0".
 * Returns the short reason key (e.g. "NO_MAP_COVERAGE") or "INVALID" if unparseable.
 */
export const parseSdsMessageReason = (
  message: string | null | undefined,
): string => {
  if (!message) return 'INVALID'
  const match = message.match(/VALIDATION_FAILURE_REASON_(\w+)/)
  if (match) return match[1]
  if (message.toLowerCase().includes('no map coverage'))
    return 'NO_MAP_COVERAGE'
  if (message.toLowerCase().includes('segment too short'))
    return 'SEGMENT_TOO_SHORT'
  return 'INVALID'
}

/** Patterns used to match returned Mobileye map names to a metropolis (fallback for multi-map responses). */
export const METROPOLIS_MAP_NAME_PATTERNS: Record<string, string[]> = {
  Hamburg: ['hamburg'],
  Munich: ['munich'],
  Austin: ['austin'],
  'Los Angeles': ['_la_', 'angeles'],
  'Lake Nona': ['lake_nona'],
}

/**
 * Return the map validation entry to use for the given metropolis.
 *
 * When only one non-mock validation is returned, use it directly.
 * When multiple are returned, fall back to metro pattern filtering.
 * Returns null when no usable match is found.
 */
export const findMobileyeMapValidation = <
  T extends { mapName: string; passed: boolean },
>(
  mapValidations: T[],
  metropolisName: string | undefined,
): T | null => {
  const nonMock = mapValidations.filter(
    v => !v.mapName.toLowerCase().includes('mockmap'),
  )
  if (nonMock.length === 0) return null
  if (nonMock.length === 1) return nonMock[0]

  // Multiple non-mock validations — filter by metro pattern
  if (!metropolisName) return nonMock[0]
  const patterns = METROPOLIS_MAP_NAME_PATTERNS[metropolisName]?.map(p =>
    p.toLowerCase(),
  )
  if (!patterns) return nonMock[0]
  return (
    nonMock.find(v =>
      patterns.some(pattern => v.mapName.toLowerCase().includes(pattern)),
    ) ?? nonMock[0]
  )
}

// ── VTR ticket / metropolis matching ────────────────────────────────────────────

/**
 * Whether a VTR ticket's location belongs to the currently selected metropolis.
 *
 * - No metropolis selected (undefined) → always matches (show all tickets).
 * - Blank/whitespace location → never matches once a metropolis is selected
 *   (a ticket with no location cannot be attributed to a city).
 * - Otherwise a case-insensitive, bidirectional substring match, so
 *   "Munich" matches "Munich Test Track" and vice-versa.
 */
export const vtrLocationMatchesCity = (
  location: string | null | undefined,
  metropolisName: string | undefined,
): boolean => {
  if (metropolisName == null) return true
  const loc = (location ?? '').trim().toLowerCase()
  if (loc === '') return false
  const city = metropolisName.toLowerCase()
  return city.includes(loc) || loc.includes(city)
}

// ── Map padding layout constants ───────────────────────────────────────────────

export const SIDEBAR_WIDTH = 380
export const PANEL_WIDTH = 340
// The editor form card (create/edit flows) is a touch wider than the overview
// details panel to give the waypoint coordinate inputs more room.
export const EDITOR_PANEL_WIDTH = 350
export const PANEL_GAP = 16

export type MapPaddingRect = {
  top: number
  right: number
  bottom: number
  left: number
}

/**
 * Compute the map viewport padding used when focusing a route.
 *
 * On the main route-builder screen the map is always an inset card sitting
 * *beside* the sidebar, the editor card, or the details panel — nothing overlaps
 * it on route focus, so the fit gets symmetric breathing room in every mode
 * (`fullBleedMap === false` → left = 60).
 *
 * `fullBleedMap` is the assign-trip modal, where the map spans the whole modal
 * *under* the flush sidebar / floating editor panel (and the details panel
 * floats over it). There the left padding must clear whichever overlay is
 * showing so the framed route isn't hidden behind it — the wider editor-panel
 * clearance in editor mode, otherwise the sidebar, plus the details panel when a
 * route is selected (it floats to the right of the sidebar over the map).
 */
export const computeMapPadding = (
  inEditor: boolean,
  fullBleedMap = false,
  detailsPanelShowing = false,
): MapPaddingRect => {
  const top = 60
  const bottom = 60
  const right = 60

  // Full-page: the map is beside the panel in every mode, so no left overlay to
  // clear. Only the full-bleed modal overlays the map and needs the reserve.
  const left = fullBleedMap
    ? inEditor
      ? PANEL_WIDTH + PANEL_GAP * 2
      : // With a route selected, the details panel floats at left = SIDEBAR_WIDTH
        // + PANEL_GAP with width PANEL_WIDTH; clear its right edge plus one gap.
        detailsPanelShowing
        ? SIDEBAR_WIDTH + PANEL_WIDTH + PANEL_GAP * 2
        : SIDEBAR_WIDTH + PANEL_GAP
    : 60

  return { top, right, bottom, left }
}

/**
 * `map.cameraForBounds` returns `undefined` — logging "Map cannot fit within
 * canvas with the given bounds, padding, and/or offset" — whenever the requested
 * padding leaves no usable space on the canvas. This bites the assign-trip modal:
 * its map is much narrower than the full-screen route builder, so the same panel
 * padding (left + right ≈ 1016px) exceeds the modal canvas width and the fit
 * silently no-ops.
 *
 * This wrapper first tries the requested padding, then falls back to padding
 * that fits the canvas while keeping the route in the SAME horizontal position
 * as the full-screen view. The camera's centre offset is governed by the
 * difference `left − right` (left ≈ sidebar + details panel, right ≈ overlay
 * options), not their ratio — so to fit a narrower canvas we shrink the *sum* by
 * subtracting an equal amount from both sides, preserving `left − right` exactly.
 * Scaling proportionally instead would shrink that difference and pull the route
 * left. A small symmetric padding is the final fallback.
 */
export const cameraForBoundsWithPaddingFallback = (
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBoundsLike,
  padding: MapPaddingRect,
  options?: { maxZoom?: number; bearing?: number; pitch?: number },
): ReturnType<maplibregl.Map['cameraForBounds']> => {
  const { maxZoom = 14, bearing = 0, pitch = 0 } = options ?? {}
  const tryCamera = (p: MapPaddingRect) =>
    map.cameraForBounds(bounds, { padding: p, maxZoom, bearing, pitch })

  // cameraForBounds adds the map transform's *stored* padding on top of the
  // padding option. A prior flyTo({padding}) (e.g. the modal's vehicle-focus
  // camera) leaves padding in the transform, so the panel padding we request
  // stacks on top of it, overflows the canvas, and cameraForBounds returns
  // undefined. Zero the stored padding first so only the padding we pass counts.
  // Also resize() so the transform matches the current (modal) canvas size.
  map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 })
  map.resize()

  const camera = tryCamera(padding)
  if (camera) return camera

  // getCanvas().clientWidth/Height are CSS pixels, matching the padding units.
  const canvas = map.getCanvas()
  const width = canvas.clientWidth
  const height = canvas.clientHeight
  if (width > 0 && height > 0) {
    const MIN_PAD = 20
    // Reserve at least ~40% of each axis as usable (padding-free) space.
    // Shrink each axis by subtracting an equal amount from both sides so the
    // centre offset (left − right, top − bottom) is preserved. If one side would
    // fall below MIN_PAD, pin it and take the remainder from the other side.
    const shrinkAxis = (
      a: number,
      b: number,
      extent: number,
    ): [number, number] => {
      const budget = extent * 0.6
      const total = a + b
      if (total <= budget) return [a, b]
      const excess = total - budget
      let a2 = a - excess / 2
      let b2 = b - excess / 2
      if (a2 < MIN_PAD) {
        a2 = MIN_PAD
        b2 = Math.max(MIN_PAD, budget - a2)
      } else if (b2 < MIN_PAD) {
        b2 = MIN_PAD
        a2 = Math.max(MIN_PAD, budget - b2)
      }
      return [a2, b2]
    }
    const [left, right] = shrinkAxis(padding.left, padding.right, width)
    const [top, bottom] = shrinkAxis(padding.top, padding.bottom, height)
    const shrunkCamera = tryCamera({ top, right, bottom, left })
    if (shrunkCamera) return shrunkCamera
  }

  return tryCamera({ top: 40, right: 40, bottom: 40, left: 40 })
}

// ── ODD element display helpers ───────────────────────────────────────────────

/** Human-readable label for each ODD type short-key. */
export const ODD_TYPE_LABELS: Record<string, string> = {
  STOP_SIGN: 'Stop Sign',
  TRAFFIC_LIGHT: 'Traffic Light',
  YIELD_SIGN: 'Yield Sign',
  ROUNDABOUT: 'Roundabout',
  PEDESTRIAN_CROSSING: 'Pedestrian Crossing',
  RAILROAD_CROSSING: 'Railroad Crossing',
  NO_ENTRY: 'No Entry',
  NO_TURN_ON_RED: 'No Turn on Red',
  ONE_WAY: 'One Way',
  OPS_HUB: 'Ops Hub',
  ALL_WAY_STOP: 'All-Way Stop',
  BUS_STOP: 'Bus Stop',
  RIGHT_ON_RED: 'Right on Red',
  SCHOOL_ZONE: 'School Zone',
  OTHER: 'Others',
}

/**
 * Aggregate ODDMapElement[] into sorted [type, count] pairs.
 * Sorted by count descending; 'OTHER' type is always last.
 */
export const aggregateOddElements = (
  elements: { type: string }[],
): [string, number][] => {
  const byType: Record<string, number> = {}
  for (const el of elements) {
    byType[el.type] = (byType[el.type] ?? 0) + 1
  }
  return Object.entries(byType).sort(([aType, aCount], [bType, bCount]) => {
    if (aType === 'OTHER') return 1
    if (bType === 'OTHER') return -1
    return bCount - aCount
  })
}

// ── Trip type helpers ─────────────────────────────────────────────────────────

import { TripType } from '@backoffice-frontend/graphql'

/**
 * Both enum values are labelled "V&V" throughout the Route Builder, so anything
 * gated on V&V must accept either — routes created in Overwatch carry
 * TripTypeInternal, routes created here carry TripTypeVnv.
 */
export const isVnvTripType = (tripType: TripType): boolean =>
  tripType === TripType.TripTypeVnv || tripType === TripType.TripTypeInternal

// ── Saved-route conversion helpers ────────────────────────────────────────────

import type { Waypoint } from './TripEditor/types'

/**
 * Convert a saved-route details segment list into the flat Waypoint array
 * used by TripEditorView / WaypointList.
 *
 * Each segment contributes its destination (or stop desiredLocation) as a
 * waypoint. The first segment always uses the stop desiredLocation when
 * present (the origin stop), subsequent ones use the destination.
 */
export const segmentsToWaypoints = (
  segments: Array<{
    segment:
      | {
          id: string
          destination:
            | { latitude: number; longitude: number }
            | null
            | undefined
          stop:
            | {
                desiredLocation:
                  | { latitude: number; longitude: number }
                  | null
                  | undefined
              }
            | null
            | undefined
        }
      | null
      | undefined
  }>,
): Waypoint[] => {
  const waypoints: Waypoint[] = []

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]?.segment
    if (!seg) continue

    const loc =
      i === 0 && seg.stop?.desiredLocation
        ? seg.stop.desiredLocation
        : (seg.destination ?? seg.stop?.desiredLocation)

    if (!loc) continue

    waypoints.push({
      id: seg.id || crypto.randomUUID(),
      lat: loc.latitude,
      lng: loc.longitude,
      address: '',
      validated: true,
      oddElementType: null,
      desiredLat: loc.latitude,
      desiredLng: loc.longitude,
      heading: null,
    })
  }

  // Deduplicate consecutive identical coordinates (can occur at origin=first
  // waypoint when stop.desiredLocation === destination).
  return waypoints.filter((w, idx) => {
    if (idx === 0) return true
    const prev = waypoints[idx - 1]
    return !(prev.lat === w.lat && prev.lng === w.lng)
  })
}

// ── Waypoint list overflow measurement ───────────────────────────────────────

/** A fresh measurement claims overflow once the content exceeds the box by this much. */
const OVERFLOW_CLAIM_PX = 2
/**
 * Slack required before an established overflow claim is given up again.
 *
 * One wrapped line of address text is ~20px, so 24 is "a whole line's worth of
 * room appeared, not just a scrollbar's width" — see the loop described on
 * `nextWaypointOverflowState`.
 */
const OVERFLOW_RELEASE_PX = 24

/**
 * Whether the waypoint list should report itself as overflowing its clipped
 * container, given the previous answer and a fresh measurement.
 *
 * Asymmetric on purpose. The answer feeds the layout that produces it: `true`
 * puts `overflow: hidden` on the scroll container, `false` puts `overflow: auto`
 * there, and `auto` is what lets a scrollbar appear. A scrollbar narrows the
 * column, a narrower column wraps an address onto another line, and another line
 * is enough to overflow — which flips the answer back to `true`, removes the
 * scrollbar, unwraps the address, and flips it to `false` again. Measuring on
 * every render (which the caller deliberately does, to stay current through the
 * expand animation) turns that standoff into an unbounded render loop and React
 * kills the tree with "Maximum update depth exceeded".
 *
 * Requiring real slack before *releasing* the claim breaks the cycle. When the
 * two layouts genuinely disagree the list stays clipped with "Show all"
 * available, which is the harmless side to fail on: an unnecessary "Show all" is
 * a button nobody needs, a crash loses the route being built.
 */
export const nextWaypointOverflowState = (
  previous: boolean | null,
  scrollHeight: number,
  clientHeight: number,
): boolean => {
  const slack = clientHeight - scrollHeight
  return previous === true
    ? slack < OVERFLOW_RELEASE_PX
    : slack < -OVERFLOW_CLAIM_PX
}

// ── Saved-trip update helpers ─────────────────────────────────────────────────

import {
  DestinationType,
  type TripSegmentInput,
  TripStopType,
} from '@backoffice-frontend/graphql'
import type {
  RouteBuilderSavedRouteDetailsQuery,
  RouteBuilderUpdateSavedTripMutationVariables,
} from './TripEditor/TripEditor.hook'

type SavedRouteDetails = NonNullable<
  RouteBuilderSavedRouteDetailsQuery['savedRouteDetails']
>

/**
 * Marks the trip as written by this UI. Every create/update from the Route
 * Builder carries it, so it has to be resent on partial edits too — see
 * buildUpdateSavedTripVariables.
 */
export const UI_CLIENT_METADATA = [['client', 'Web UI']]

/** Convert saved route details segments into TripSegmentInput for re-validation.
 * Intentionally omits snappedLocation so the router snaps from desiredLocation,
 * matching the original edit-time validation that produced the saved route. */
export const detailsToSegmentInputs = (
  trip: SavedRouteDetails | null | undefined,
  tripType: TripType,
): TripSegmentInput[] =>
  (trip?.trip?.segments ?? [])
    .map(({ segment }) => segment)
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((seg, i) => {
      const dest = seg.stop?.desiredLocation ??
        seg.destination ?? { latitude: 0, longitude: 0 }
      return {
        id: seg.id,
        sequenceId: seg.sequenceId ?? i + 1,
        destination: { latitude: dest.latitude, longitude: dest.longitude },
        stop: {
          name: seg.stop?.desiredAddress || `Waypoint ${i + 1}`,
          desiredLocation: {
            latitude: dest.latitude,
            longitude: dest.longitude,
          },
          desiredAddress: seg.stop?.desiredAddress,
          destinationBehavior: {
            stopType:
              seg.stop?.destinationBehavior?.stopType ??
              TripStopType.StopTypeNormal,
            types: seg.stop?.destinationBehavior?.types ?? [
              i === 0 && tripType === TripType.TripTypeGoods
                ? DestinationType.DestinationTypeGoodsPickUp
                : tripType === TripType.TripTypeGoods
                  ? DestinationType.DestinationTypeGoodsDropOff
                  : DestinationType.DestinationTypeRepositioning,
            ],
          },
          oddElementType: seg.stop?.oddElementType ?? undefined,
        },
        numPassengers: 0,
        protected: true,
        metadata: seg.metadata ?? [],
      }
    })

/**
 * Build the variables for RouteBuilderUpdateSavedTrip.
 *
 * `updateSavedTrip` replaces the stored trip wholesale, so every caller has to
 * send the *complete* state — a field left out is silently cleared (a rename
 * that omits allWaypointsMode switches sequential dispatch off, one that omits
 * assignedVtrs drops the VTR links). Every partial edit therefore goes through
 * this one builder, so a field added to a trip only has to be threaded here.
 */
export const buildUpdateSavedTripVariables = ({
  id,
  tripId,
  name,
  description,
  tripType,
  segments,
  assignedVtrs,
  allWaypointsMode,
}: {
  id: string
  /** Falls back to `id`; the editor keeps the original tripId when it has one. */
  tripId?: string | null
  name: string
  description?: string | null
  tripType: TripType
  segments: TripSegmentInput[]
  assignedVtrs?:
    | readonly { vtr: string; codebeamerIds: readonly string[] }[]
    | null
  allWaypointsMode: boolean
}): RouteBuilderUpdateSavedTripMutationVariables => ({
  id,
  name: name.trim(),
  tripId: tripId ?? id,
  type: tripType,
  segments,
  description: description?.trim() || undefined,
  metadata: UI_CLIENT_METADATA,
  assignedVtrs: assignedVtrs?.map(({ vtr, codebeamerIds }) => ({
    vtr,
    codebeamerIds: [...codebeamerIds],
  })),
  allWaypointsMode,
})

// ── Dispatch status display ───────────────────────────────────────────────────

import { DispatchingStatus } from '@backoffice-frontend/graphql'

export type DispatchStatusDisplay = {
  label: string
  /** pace-web Badge variant. */
  variant: 'neutral' | 'success' | 'warning' | 'critical'
  /** True while dispatching is still in flight (non-terminal). */
  inProgress: boolean
}

/**
 * Map a backend `DispatchingStatus` to a user-facing pill label + Badge variant.
 * Returns `null` for statuses that should not surface a pill (INVALID / unknown),
 * so callers can simply skip rendering when there is nothing meaningful to show.
 */
export const getDispatchStatusDisplay = (
  status: DispatchingStatus | null | undefined,
): DispatchStatusDisplay | null => {
  switch (status) {
    case DispatchingStatus.DispatchingStatusRouteValidating:
      return {
        label: 'Validating route…',
        variant: 'neutral',
        inProgress: true,
      }
    case DispatchingStatus.DispatchingStatusRouteValidationSuccess:
      return { label: 'Route validated', variant: 'neutral', inProgress: true }
    case DispatchingStatus.DispatchingStatusRouteDispatching:
      return { label: 'Dispatching…', variant: 'neutral', inProgress: true }
    case DispatchingStatus.DispatchingStatusRouteDispatchSuccess:
      return { label: 'Dispatched', variant: 'success', inProgress: false }
    case DispatchingStatus.DispatchingStatusRouteValidationFailed:
      return {
        label: 'Validation failed',
        variant: 'critical',
        inProgress: false,
      }
    case DispatchingStatus.DispatchingStatusRouteDispatchFailed:
      return {
        label: 'Dispatch failed',
        variant: 'critical',
        inProgress: false,
      }
    default:
      return null
  }
}
